import React, { useState } from 'react'
import { Button, Dropdown, Menu, Icon, Input, Tooltip } from 'antd'
import SortableShotList from './ShortableShotList'
import VoiceoverModal from './/VoiceoverModal'
import SceneDurationFeedback from './SceneDurationFeedback'

const { TextArea } = Input

const SceneContainer = ({
  scene,
  isLoading,
  formData,
  setFormData,
  updateSceneData,
  addShotToScene,
  handleDeleteScene,
  handleDeleteShot,
  handleRegenerateImage,
  handleRegenerateWithReference,
  handleRegenerateWithReferenceNanoBanana,
  handleEditSettings,
  updateShotDataRef,
  handleSortEnd,
  handleCopyImageToShot,
  generateVoiceForScene,
  loadingVoices,
  voices = [],
  onRefreshVoices,
  isClusteringAudio,
  audioClusteringProgress,
  audioClusteringStatus,
  handleFileUpload,
  isUploadingImage,
  copyImageTargets,
  frameCopyTargets,
  imageProvider,
  isFetchingCharacters,
  videoId,
  onStopGeneration,
  onRestoreAudio,
  shotViewMode = 'expanded',
  ...props
}) => {
  const [voiceModalVisible, setVoiceModalVisible] = useState(false)
  const [openHistoryOnModalOpen, setOpenHistoryOnModalOpen] = useState(false)

  const audioHistoryCount = Array.isArray(scene?.audio_history)
    ? scene.audio_history.filter(item => item?.url).length
    : 0
  const sceneVoiceoverData =
    scene?.aggregated_voiceover && typeof scene.aggregated_voiceover === 'object'
      ? scene.aggregated_voiceover
      : null
  const isSceneAudioGenerating = Boolean(
    sceneVoiceoverData?.isGenerating || isClusteringAudio
  )

  const handleOpenVoiceModal = () => {
    setOpenHistoryOnModalOpen(false)
    setVoiceModalVisible(true)
  }

  const handleCloseVoiceModal = () => {
    setOpenHistoryOnModalOpen(false)
    setVoiceModalVisible(false)
  }

  const handleOpenAudioHistory = () => {
    setOpenHistoryOnModalOpen(true)
    setVoiceModalVisible(true)
  }

  const handleVoiceGenerate = async payload => {
    const sceneId = scene.id

    try {
      const outputUrl = await generateVoiceForScene(sceneId, payload)
      // Success update handled in useVoiceOver hook (updating scene.audio_link)
      // We can just return the url here
      return outputUrl
    } catch (err) {
      // Error logging handled in hook or console
      throw err
    }
  }

  const isDialogueMode = formData.audioMode === 'dialogue'

  const handleDeleteAudioHistory = index => {
    const updatedHistory = [...(scene.audio_history || [])]
    updatedHistory.splice(index, 1)
    updateSceneData(scene.id, 'audio_history', updatedHistory)
  }

  const handleStopSceneAudioGeneration = () => {
    if (onStopGeneration) {
      onStopGeneration()
    }

    if (sceneVoiceoverData?.isGenerating) {
      updateSceneData(scene.id, 'aggregated_voiceover', {
        ...sceneVoiceoverData,
        isGenerating: false,
        progress: 0,
        status: 'stopped',
        error: null
      })
    }
  }

  return (
    <div className={`scene-container scene-container--${shotViewMode}`}>
      <div className="left-panel">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0 }}>{scene.title}</h2>
            <SceneDurationFeedback scene={scene} />

            {!isDialogueMode && (
              <>
                <Tooltip
                  title={
                    isSceneAudioGenerating
                      ? 'Audio Generation in progress...'
                      : 'Add Audio'
                  }
                >
                  <Button
                    type="primary"
                    ghost
                    size="small"
                    onClick={handleOpenVoiceModal}
                    // disabled={isClusteringAudio}
                    style={{
                      height: 32,
                      marginLeft: 4,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Audio{' '}
                    <Icon
                      type={isSceneAudioGenerating ? 'loading' : 'sound'}
                    />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="add-shot"
                    onClick={() => addShotToScene(scene.id)}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <Icon type="plus" style={{ verticalAlign: 'middle' }} />
                      Add Shot
                    </span>
                  </Menu.Item>
                  <Menu.Item
                    key="delete-scene"
                    onClick={() => handleDeleteScene(scene.id)}
                  >
                    <Icon type="delete" /> Delete Scene
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <Button type="link" size="small">
                Action <Icon type="down" />
              </Button>
            </Dropdown>
          </div>
        </div>
        <TextArea
          value={scene.description}
          rows={2}
          readOnly={isLoading}
          onChange={e => {
            const newDescription = e.target.value
            updateSceneData(scene.id, 'description', newDescription)
          }}
        />
      </div>
      <div className="right-panel">
        <SortableShotList
          shots={scene.shots}
          sceneId={scene.id}
          formData={formData}
          setFormData={setFormData}
          handleDeleteShot={handleDeleteShot}
          handleRegenerateImage={handleRegenerateImage}
          handleRegenerateWithReference={handleRegenerateWithReference}
          handleRegenerateWithReferenceNanoBanana={
            handleRegenerateWithReferenceNanoBanana
          }
          handleEditSettings={handleEditSettings}
          updateShotDataRef={updateShotDataRef}
          onSortEnd={params => handleSortEnd(scene.id, params)}
          axis="x"
          useDragHandle
          distance={1}
          helperClass="sortable-helper"
          lockAxis="x"
          lockToContainerEdges
          handleFileUpload={handleFileUpload}
          handleCopyImageToShot={handleCopyImageToShot}
          isUploadingImage={isUploadingImage}
          copyImageTargets={copyImageTargets}
          frameCopyTargets={frameCopyTargets}
          imageProvider={imageProvider}
          videoId={videoId}
          isFetchingCharacters={isFetchingCharacters}
          shotViewMode={shotViewMode}
        />
      </div>

      <VoiceoverModal
        visible={voiceModalVisible}
        isClusteringAudio={isClusteringAudio}
        isSceneAudioGenerating={isSceneAudioGenerating}
        scene={scene}
        voices={voices}
        videoId={videoId}
        loadingVoices={loadingVoices}
        formData={formData}
        selectedVoice={formData?.selectedVoice}
        characterMap={formData?.characterMap}
        characterVoices={formData?.characterVoices}
        onClose={handleCloseVoiceModal}
        onGenerate={handleVoiceGenerate}
        onRefreshVoices={onRefreshVoices}
        onNavigateToStep={props.onNavigateToStep}
        onSceneStructureChange={props.onSceneStructureChange}
        audioClusteringProgress={audioClusteringProgress}
        audioClusteringStatus={audioClusteringStatus}
        audioMode={formData.audioMode}
        onStopGeneration={handleStopSceneAudioGeneration}
        onRestoreAudio={onRestoreAudio}
        onDeleteAudioHistory={handleDeleteAudioHistory}
        openHistoryOnOpen={openHistoryOnModalOpen}
      />
    </div>
  )
}

export default SceneContainer
